import React, { useState, useEffect } from 'react';
import { MapPin, Building2, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { AuctionModal } from '../components/AuctionModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BrowseByState = () => {
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState(null);
  const [cities, setCities] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetchStates();
  }, []);

  const fetchStates = async () => {
    try {
      const response = await axios.get(`${API}/states`);
      setStates(response.data.states);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  const handleStateClick = async (state) => {
    setSelectedState(state);
    setSelectedCity(null);
    setLoading(true);

    try {
      // Fetch cities in this state
      const citiesResponse = await axios.get(`${API}/states/${state}/cities`);
      setCities(citiesResponse.data.cities);

      // Fetch all auctions in this state
      const auctionsResponse = await axios.post(`${API}/auctions/search`, {
        state: state,
        limit: 500
      });
      setAuctions(auctionsResponse.data.auctions);
    } catch (error) {
      console.error('Error fetching state data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCityClick = async (city) => {
    setSelectedCity(city);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auctions/search`, {
        state: selectedState,
        city: city,
        limit: 500
      });
      setAuctions(response.data.auctions);
    } catch (error) {
      console.error('Error fetching city auctions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAuctionClick = (auction, event) => {
    event.stopPropagation(); // Prevent event bubbling
    console.log('Auction clicked:', auction);
    setSelectedAuction(auction);
    setModalOpen(true);
  };

  return (
    <div className="bg-gray-50 min-h-screen py-12" data-testid="browse-by-state-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Browse Auctions by State
          </h1>
          <p className="text-lg text-gray-600">
            Find auction houses near you organized by state and city
          </p>
        </div>



        <div className="mt-8 grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* States List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-4">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Select State</h2>
              <div className="max-h-[600px] overflow-y-auto">
                <div className="space-y-2">
                  {states.map((state) => (
                    <button
                      key={state.state}
                      onClick={() => handleStateClick(state.state)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center justify-between ${
                        selectedState === state.state
                          ? 'bg-blue-900 text-white'
                          : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <span className="font-semibold">{state.state}</span>
                      <span className={`text-sm px-2 py-1 rounded-full ${
                        selectedState === state.state
                          ? 'bg-blue-700'
                          : 'bg-gray-200'
                      }`}>
                        {state.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Cities and Auctions */}
          <div className="lg:col-span-3">
            {!selectedState ? (
              <div className="bg-white rounded-lg shadow-lg p-12 text-center">
                <MapPin className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Select a State
                </h3>
                <p className="text-gray-600">
                  Choose a state from the list to view auction houses
                </p>
              </div>
            ) : (
              <>
                {/* Cities */}
                {cities.length > 0 && (
                  <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      Cities in {selectedState}
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      <button
                        onClick={() => handleStateClick(selectedState)}
                        className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                          !selectedCity
                            ? 'bg-amber-500 text-white'
                            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                        }`}
                      >
                        All Cities
                      </button>
                      {cities.map((city) => (
                        <button
                          key={city.city}
                          onClick={() => handleCityClick(city.city)}
                          className={`px-4 py-2 rounded-lg transition-colors flex items-center justify-between ${
                            selectedCity === city.city
                              ? 'bg-amber-500 text-white'
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                        >
                          <span className="font-semibold truncate">{city.city}</span>
                          <span className="text-xs ml-2">({city.count})</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Auction Results */}
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {selectedCity ? `${selectedCity}, ` : ''}{selectedState} Auction Houses
                    <span className="text-gray-600 text-lg ml-2">({auctions.length})</span>
                  </h2>

                  {loading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto"></div>
                      <p className="text-gray-600 mt-4">Loading auctions...</p>
                    </div>
                  ) : auctions.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {auctions.map((auction) => (
                        <div
                          key={auction.id}
                          onClick={(e) => handleAuctionClick(auction, e)}
                          className="border border-gray-200 rounded-lg p-4 hover:shadow-lg hover:border-blue-500 transition-all cursor-pointer"
                        >
                          <h3 className="text-lg font-bold text-gray-900 mb-2">
                            {auction.name}
                          </h3>
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                            {auction.description}
                          </p>
                          <div className="flex flex-wrap gap-2 mb-3">
                            {auction.categories.slice(0, 2).map((cat, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded"
                              >
                                {cat}
                              </span>
                            ))}
                            {auction.categories.length > 2 && (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                                +{auction.categories.length - 2}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <MapPin className="h-4 w-4 mr-1" />
                            {auction.location.city}, {auction.location.state}
                          </div>
                          {auction.phone && (
                            <div className="text-sm text-gray-600 mt-1">
                              📞 {auction.phone}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No auction houses found</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Auction Modal */}
      <AuctionModal 
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        auction={selectedAuction}
      />
    </div>
  );
};
